import { dayLabel, fromIsoDay, hourLabel, isoDay, shortDayLabel } from 'helper/timeLabels';

/**
 * What the record pages (a company, a group, a contact) show of their data,
 * in the texts `text` reads (record.*). Pure.
 */

// The badge of where a company stands.
export const STAGE_BADGES = { prospect: 'WARNING', client: 'VALID', lost: 'PRIMARY' };

const wholeEurosFormat = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
// « 12 400 € », its spaces unbreakable: an amount never splits across lines.
export const wholeEuros = (amount) => wholeEurosFormat.format(amount || 0).replace(/\u202f/g, '\u00a0');

// « 123 456 789 00012 »
export const formatSiret = (siret) => {
  const digits = String(siret ?? '').replace(/\D/g, '');
  return digits.length === 14 ? `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}` : digits;
};

export const fullName = (person) => [person?.firstname, person?.lastname].filter(Boolean).join(' ');

// « 8 Nov. 2025 › 15 Nov. 2025 », the period of a mission.
const longDate = (iso) => {
  const date = new Date(iso);
  const month = date.toLocaleDateString('fr-FR', { month: 'short' });
  return `${date.getDate()} ${month.charAt(0).toUpperCase()}${month.slice(1)} ${date.getFullYear()}`;
};
export const missionPeriod = (mission) => `${longDate(mission.startsOn)} › ${longDate(mission.endsOn)}`;

/**
 * The three numbers at the top of a company or a group: its missions under
 * way, its revenue of the last 12 months against the 12 before, and what is
 * left to pay. `invoices` is null when the user may not see them, or while
 * they are added up (`invoicesLoading`).
 */
export const kpiTiles = ({ current, toFill, invoices, invoicesLoading = false, stage }, text) => {
  const growth = invoices?.revenuePrev ? Math.round(((invoices.revenue - invoices.revenuePrev) / invoices.revenuePrev) * 100) : null;
  const missing = text(invoicesLoading ? 'kpiLoading' : 'kpiNoInvoices');
  let revenueDetail = missing;
  if (invoices) {
    if (growth !== null) revenueDetail = text('kpiGrowth', { growth: `${growth >= 0 ? '+' : ''}${growth}` });
    else revenueDetail = text(stage === 'client' ? 'kpiFirstYear' : 'kpiNotClient');
  }
  return [
    {
      label: text('kpiMissions'),
      value: String(current),
      detail: toFill ? text(toFill > 1 ? 'kpiToFillMany' : 'kpiToFillOne', { count: toFill }) : text('kpiAllFilled'),
    },
    { label: text('kpiRevenue'), value: invoices ? wholeEuros(invoices.revenue) : '-', detail: revenueDetail },
    {
      label: text('kpiUnpaid'),
      value: invoices ? wholeEuros(invoices.unpaidAmount) : '-',
      detail: !invoices
        ? missing
        : invoices.unpaidCount
          ? text(invoices.unpaidCount > 1 ? 'kpiUnpaidMany' : 'kpiUnpaidOne', { count: invoices.unpaidCount })
          : text('kpiNoDelay'),
    },
  ];
};

/**
 * What comes next with a company: its next appointment, task or callback.
 * `contactName`: the person of the callback.
 */
export const nextActionView = (action, { text, contactName, nameOf, now }) => {
  if (!action) return null;
  if (action.kind === 'task') {
    return { kind: 'task', title: action.title, detail: dayLabel(fromIsoDay(action.dueOn)) };
  }
  const at = new Date(action.at);
  const when = text('nextWhen', { day: isoDay(at) === isoDay(now) ? text('today') : dayLabel(at), time: hourLabel(at) });
  if (action.kind === 'callback') {
    return { kind: 'callback', title: text('nextCallback', { name: contactName || text('switchboard') }), detail: when };
  }
  return {
    kind: 'appointment',
    title: contactName ? text('nextMeeting', { name: contactName }) : text('nextMeetingAlone'),
    detail: when,
    owner: nameOf?.(action.ownerId),
  };
};

/**
 * « À venir » and « Derniers échanges » of an overview: the next three and
 * the last three exchanges (the client's own actions stay in the activity).
 */
export const exchangesOf = (items, count = 3) => {
  const exchanges = items.filter((item) => item.kind !== 'client');
  return {
    upcoming: exchanges.filter((item) => item.future).sort((a, b) => a.at - b.at).slice(0, count),
    recent: exchanges.filter((item) => !item.future).sort((a, b) => b.at - a.at).slice(0, count),
  };
};

// The date of an exchange in a list: its own words, or its day.
export const exchangeWhen = (item) => item.when ?? shortDayLabel(isoDay(item.at));
